import type { ProjectApiDTO } from '@testsa-com/common/src/codegen/generated'
import type { PanelSheetFilter } from '@testsa-com/common/src/types/Panel.type'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  IAppFiltersPollsters,
  IAppFiltersProjectPollsters,
  IAppFiltersResources,
} from '../types/App.type'
import type { IEquipmentStaticFilters } from '../types/Equipments.type'
import type {
  IContractsStaticFilters,
  IDocumentsStaticFilters,
  IPayablesStaticFilters,
  IPayoutsStaticFilters,
} from '../types/HR.type'

type AppStoreTypeFilters = {
  equipments?: IEquipmentStaticFilters
  contracts?: IContractsStaticFilters
  payables?: IPayablesStaticFilters
  payouts?: IPayoutsStaticFilters
  pollsterDocuments?: IDocumentsStaticFilters
  pollsters?: Partial<IAppFiltersPollsters>
  projectPollsters?: Partial<IAppFiltersProjectPollsters>
  resources?: Partial<IAppFiltersResources>
}

type AppStoreType = {
  currentProjectId?: ProjectApiDTO['id']
  filters: AppStoreTypeFilters
  setFilterValue: <
    T extends keyof AppStoreType['filters'],
    K extends IEquipmentStaticFilters | IAppFiltersProjectPollsters,
  >(
    fg: T,
    fk: keyof K,
    fv: string | boolean | undefined,
  ) => void
  setPollstersFilterValue: (
    fk: keyof IAppFiltersPollsters,
    fv: string | string[] | boolean | undefined,
  ) => void
  setEquipmentsFilterValue: (
    fk: keyof IEquipmentStaticFilters,
    fv: string | boolean | undefined,
  ) => void
  setProjectPollstersFilterValue: (
    fk: keyof IAppFiltersProjectPollsters,
    fv: string | boolean | undefined,
  ) => void
  setResourcesFilterValue: (
    fk: keyof IAppFiltersResources,
    fv: PanelSheetFilter[] | undefined,
  ) => void
  reset: () => void
}

const APP_KEY = 'tsa-app'

export const useAppStore = create(
  persist<AppStoreType>(
    (set, _, store) => ({
      currentProjectId: undefined,
      filters: {
        equipments: undefined,
        pollsters: {
          addressCity: undefined,
          department: undefined,
          interestedInMissions: undefined,
          withValidationErrors: undefined,
          withLeaveDate: undefined,
          withEquipment: undefined,
        },
        contracts: {
          projectIds: undefined,
          pollsterIds: undefined,
          contractTypes: undefined,
          positions: undefined,
          stateStatuses: undefined,

          dateRange: [null, null],
        },
        payables: {
          projectIds: undefined,
          pollsterIds: undefined,
          taskTypes: undefined,
          dueDates: undefined,
          status: undefined,

          dateRange: [null, null],
        },
        pollsterDocuments: {
          pollsterIds: undefined,
          documentStatuses: undefined,
        },
        projectPollsters: {
          withValidationErrors: undefined,
          withLeaveDate: undefined,
          withBriefing: undefined,
          withMissingAvailabilitySlots: undefined,
          withEnrolment: undefined,
        },
      },
      setFilterValue: (filterGroup, filterKey, filterValue) =>
        set(st => ({
          filters: {
            ...st.filters,
            [filterGroup]: {
              ...st.filters[filterGroup],
              [filterKey]: filterValue,
            },
          },
        })),
      setEquipmentsFilterValue: (filterKey, filterValue) =>
        set(st => ({
          filters: {
            ...st.filters,
            equipments: {
              ...st.filters.equipments,
              [filterKey]: filterValue,
            },
          },
        })),
      setPollstersFilterValue: (filterKey, filterValue) =>
        set(st => ({
          filters: {
            ...st.filters,
            pollsters: {
              ...st.filters.pollsters,
              [filterKey]: filterValue,
            },
          },
        })),
      setProjectPollstersFilterValue: (filterKey, filterValue) =>
        set(st => ({
          filters: {
            ...st.filters,
            projectPollsters: {
              ...st.filters.projectPollsters,
              [filterKey]: filterValue,
            },
          },
        })),
      setResourcesFilterValue: (filterKey, filterValue) =>
        set(st => ({
          filters: {
            ...st.filters,
            resources: {
              ...st.filters.resources,
              [filterKey]: filterValue,
            },
          },
        })),
      reset: () => {
        localStorage.removeItem(APP_KEY)
        set(() => store.getInitialState())
      },
    }),
    { name: APP_KEY },
  ),
)
